import { useSessionState } from '../../shared/hooks/session-state-hook';
import { sessionKey } from '../../shared/util/session-storage';
import PageHeading from '../../shared/components/UIElements/PageHeading';
import Icon from '../../shared/components/UIElements/Icon';
import React, { useEffect, useState, useContext, useRef } from 'react';
import { useParams, useHistory } from 'react-router-dom';

import Input from '../../shared/components/FormElements/Input';
import Button from '../../shared/components/FormElements/Button';
import ErrorModal from '../../shared/components/UIElements/ErrorModal';
import VitalsModal from '../../users/components/VitalsModal';
import NotesModal from '../../users/components/NotesModal';
import LoadingSpinner from '../../shared/components/UIElements/LoadingSpinner';
import { VALIDATOR_REQUIRE, VALIDATOR_MINLENGTH } from '../../shared/util/validators';
import { useForm } from '../../shared/hooks/form-hook';
import { useHttpClient } from '../../shared/hooks/http-hook';
import { AuthContext } from '../../shared/Context/auth-context';
import { BACKEND_URL } from '../../shared/util/backend-url';
import './PlaceForm.css';
import './UpdatePatient.css'; // New CSS file

const UpdatePatient = () => {
  const auth = useContext(AuthContext);
  const { patientId } = useParams();
  const draftKey = (resource) =>
    sessionKey(`doctor:${auth.doctorId}`, `prescription:${patientId}:${resource}`);
  const [showVitalsModal, setShowVitalsModal] = useSessionState(draftKey('vitals-open'), false);
  const [vitalsForm, setVitalsForm, clearVitals] = useSessionState(draftKey('vitals'), {
    height: '',
    weight: '',
    bloodGroup: '',
    comorbidities: '',
    allergies: '',
    pastAllergies: '',
    identificationMarks: ''
  });
  const [suggestionsState, setSuggestionsState] = useState({});
  const [availableDrugs, setAvailableDrugs] = useState([]);
  const [drugNameInput, setDrugNameInput] = useState('');
  // The medication row whose name is being typed, so search results open under it.
  const searchIndexRef = useRef(null);
  const [, setAllDrugs] = useState([]);
  const [, setFilteredDrugs] = useState([]);
  const [showNotes, setShowNotes] = useSessionState(draftKey('notes-open'), false);
  const history = useHistory();
  const { sendRequest, isLoading, error, clearError } = useHttpClient();

  const [interactionWarning, setInteractionWarning] = useState(null);

  useEffect(() => {
    // Set when the input changes again, so a slower earlier search is dropped.
    let stale = false;

    const fetchDrugs = async () => {
      if (!drugNameInput.trim()) return;

      // Only search if input length is > 3 or exact match with known drugs
      if (drugNameInput.length < 7) return;

      try {
        const response = await sendRequest(
          `${BACKEND_URL}/api/drugs/search?q=${encodeURIComponent(drugNameInput)}`,
          'GET'
        );
        const drugs = response.drugs || [];
        // RxNorm finds nothing for partial input such as "ibuprofen 2"; keep the
        // previous results so typing a strength narrows them instead.
        if (stale || drugs.length === 0) return;
        setAvailableDrugs(drugs);
        setAllDrugs(drugs);

        // The field's onChange filtered the previous results before these
        // arrived, so open the dropdown with the new ones now.
        const matches = drugs.filter((drug) =>
          drug.toLowerCase().includes(drugNameInput.toLowerCase())
        );
        const index = searchIndexRef.current;
        if (index !== null) {
          setSuggestionsState((prev) => ({
            ...prev,
            [index]: { show: matches.length > 0, suggestions: matches }
          }));
        }
      } catch (err) {
        console.error('Failed to fetch drug list:', err);
      }
    };

    const debounce = setTimeout(fetchDrugs, 300);
    return () => {
      stale = true;
      clearTimeout(debounce);
    };
  }, [drugNameInput, sendRequest]);

  const [formState, inputHandler, , clearForm, formStored] = useForm(
    {
      condition: {
        value: '',
        isValid: false
      },
      remark: {
        value: '',
        isValid: false
      }
    },
    false,
    draftKey('consultation')
  );

  const [medications, setMedications, clearMedications, medicationsStored] = useSessionState(
    draftKey('medications'),
    [{ name: '', dose: '', duration: '', frequency: '' }]
  );

  // Drugs the prescriber types to check on their own, apart from the patient's
  // history and this prescription.
  const [checkDrugs, setCheckDrugs] = useState('');
  const checkDrugNames = checkDrugs
    .split(/[,\n]/)
    .map((name) => name.trim())
    .filter(
      (name, index, names) =>
        name && names.findIndex((other) => other.toLowerCase() === name.toLowerCase()) === index
    );

  const handleMedicationChange = (index, field, value) => {
    const newMedications = [...medications];
    newMedications[index][field] = value;

    // If updating durationValue or durationUnit, recompute final duration string
    const durationValue = newMedications[index].durationValue || '';
    const durationUnit = newMedications[index].durationUnit || '';

    if (durationValue && durationUnit) {
      newMedications[index].duration = `${durationValue} ${durationUnit}`;
    } else {
      newMedications[index].duration = ''; // Clear duration if incomplete
    }

    setMedications(newMedications);
  };

  const addMedicationField = () => {
    setMedications((prev) => [...prev, { name: '', dose: '', duration: '', frequency: '' }]);
  };

  const removeMedicationField = (index) => {
    setMedications((prev) => prev.filter((_, i) => i !== index));
  };

  const savePrescription = async () => {
    try {
      const responseData = await sendRequest(
        `${BACKEND_URL}/api/doctor`,
        'POST',
        JSON.stringify({
          patient: patientId,
          doctor: auth.doctorId,
          condition: formState.inputs.condition.value,
          medication: medications, // use the full medication array
          remarks: formState.inputs.remark.value
        }),
        {
          'Content-Type': 'application/json',
          Authorization: 'Bearer ' + auth.token
        }
      );
      //console.log('Response from server:', responseData);
      const prescriptionId = responseData.history?._id;
      console.log('Prescription ID:', prescriptionId);
      //history.push(`/doctor/${auth.doctorId}`);
      clearForm();
      clearMedications();
      history.push(`/preview/${prescriptionId}`);
    } catch (err) {
      console.error('Prescription creation failed:', err);
    }
  };

  const patientUpdateSubmitHandler = async (event) => {
    event.preventDefault();

    try {
      const historyRes = await sendRequest(
        `${BACKEND_URL}/api/doctor/${patientId}`,
        'GET',
        null,
        {
          Authorization: 'Bearer ' + auth.token
        }
      );
      const fullHistory = historyRes.history || [];

      const gptCheckRes = await sendRequest(
        `${BACKEND_URL}/api/gpt`,
        'POST',
        JSON.stringify({
          newMedication: medications,
          history: fullHistory
        }),
        {
          'Content-Type': 'application/json',
          Authorization: 'Bearer ' + auth.token
        }
      );

      const reply = gptCheckRes.message;
      setInteractionWarning({ header: 'Prescription review', message: reply });
    } catch (err) {
      console.error('Prescription creation failed:', err);
    }
  };

  const drugCheckSubmitHandler = async (event) => {
    event.preventDefault();
    try {
      const drugCheckRes = await sendRequest(
        `${BACKEND_URL}/api/gpt/drugs`,
        'POST',
        JSON.stringify({ drugs: checkDrugNames }),
        {
          'Content-Type': 'application/json',
          Authorization: 'Bearer ' + auth.token
        }
      );
      setInteractionWarning({ header: 'Interaction check', message: drugCheckRes.message });
    } catch (err) {
      console.error('Interaction check failed:', err);
    }
  };

  const openVitalsModal = () => setShowVitalsModal(true);
  const closeVitalsModal = () => setShowVitalsModal(false);

  const saveVitalsHandler = async () => {
    try {
      // 1. Update the Patient Schema (Vitals)
      await sendRequest(
        `${BACKEND_URL}/api/patient/${patientId}`,
        'PATCH',
        JSON.stringify({
          height: vitalsForm.height,
          weight: vitalsForm.weight,
          bloodGroup: vitalsForm.bloodGroup
        }),
        {
          'Content-Type': 'application/json',
          Authorization: 'Bearer ' + auth.token
        }
      );

      // 2. Update the PatientInfo Schema (Additional Info)
      await sendRequest(
        `${BACKEND_URL}/api/patientinfo/${patientId}`,
        'PATCH',
        JSON.stringify({
          comorbidities: vitalsForm.comorbidities,
          allergies: vitalsForm.allergies,
          pastAllergies: vitalsForm.pastAllergies,
          identificationMarks: vitalsForm.identificationMarks
        }),
        {
          'Content-Type': 'application/json',
          Authorization: 'Bearer ' + auth.token
        }
      );

      clearVitals();
      closeVitalsModal();
    } catch (err) {
      console.error('Vitals update failed:', err);
    }
  };

  return (
    <React.Fragment>
      <ErrorModal error={error} onClear={clearError} />
      {interactionWarning && (
        <ErrorModal header={interactionWarning.header} onClear={() => setInteractionWarning(null)}>
          <div>
            <p>
              ⚠️ <strong>Drug Interaction Alert:</strong>
            </p>
            {/* pre-line keeps the reply's DrugBank findings one per line. */}
            <p style={{ marginBottom: '1rem', whiteSpace: 'pre-line' }}>
              {interactionWarning.message}
            </p>
          </div>
        </ErrorModal>
      )}
      {isLoading && <LoadingSpinner asOverlay />}
      <PageHeading
        eyebrow="PATIENT CARE / PRESCRIPTION"
        title="Create a prescription"
        description="Record the consultation and build your patient’s treatment plan."
      >
        <Button inverse type="button" onClick={openVitalsModal}>
          <Icon name="pulse" size={18} /> Vitals & info
        </Button>
        <Button inverse type="button" onClick={() => setShowNotes(true)}>
          <Icon name="file" size={18} /> Clinical notes
        </Button>
      </PageHeading>
      <p
        className={`session-save-note ${!formStored || !medicationsStored ? 'session-save-note--error' : ''}`}
        role="status"
      >
        {formStored && medicationsStored
          ? 'Your unfinished prescription is kept in this tab.'
          : 'Browser storage is unavailable. Keep this page open until you save.'}
      </p>
      <form className="drug-check" onSubmit={drugCheckSubmitHandler}>
        <label htmlFor="drug-check-input">Check drugs for interactions</label>
        <div className="drug-check__row">
          <input
            type="text"
            id="drug-check-input"
            value={checkDrugs}
            onChange={(e) => setCheckDrugs(e.target.value)}
            placeholder="e.g. warfarin, ibuprofen"
            aria-describedby="drug-check-hint"
          />
          <Button type="submit" disabled={checkDrugNames.length < 2 || isLoading}>
            Check interactions
          </Button>
        </div>
        <p id="drug-check-hint" className="drug-check__hint">
          Type two or more drugs, separated by commas. Only these drugs are checked, not the
          patient’s history or this prescription.
        </p>
      </form>
      <form className="place-form" onSubmit={patientUpdateSubmitHandler}>
        <div className="prescription-section-heading">
          <span>01</span>
          <div>
            <h2>Consultation details</h2>
            <p>Start with the condition being treated.</p>
          </div>
        </div>
        <Input
          id="condition"
          element="input"
          type="text"
          label="Condition / diagnosis"
          placeholder="Enter the presenting condition"
          validators={[VALIDATOR_REQUIRE()]}
          errorText="Please enter condition."
          onInput={inputHandler}
          initialValue={formState.inputs.condition.value}
          initialValid={formState.inputs.condition.isValid}
        />
        <div className="medication-section">
          <div className="prescription-section-heading">
            <span>02</span>
            <div>
              <h2>Medications</h2>
              <p>Set the medication, dose, duration, and frequency.</p>
            </div>
          </div>
          {medications.map((med, index) => (
            <div className="medication-group" key={index}>
              <div className="medication-row-heading">
                <span className="badge">Medication {index + 1}</span>
              </div>
              <div
                className="medication-field medication-field--name"
                style={{ position: 'relative' }}
              >
                <label htmlFor={`med-name-${index}`}>Medication name</label>
                <input
                  type="text"
                  id={`med-name-${index}`}
                  value={med.name}
                  onChange={(e) => {
                    const inputVal = e.target.value;
                    handleMedicationChange(index, 'name', inputVal);
                    searchIndexRef.current = index;
                    setDrugNameInput(inputVal);

                    const matches = availableDrugs.filter((drug) =>
                      drug.toLowerCase().includes(inputVal.toLowerCase())
                    );

                    setSuggestionsState((prev) => ({
                      ...prev,
                      [index]: { show: matches.length > 0, suggestions: matches }
                    }));
                  }}
                  onFocus={() => {
                    const matches = availableDrugs.filter((drug) =>
                      drug.toLowerCase().includes(med.name.toLowerCase())
                    );
                    setSuggestionsState((prev) => ({
                      ...prev,
                      [index]: { show: matches.length > 0, suggestions: matches }
                    }));
                  }}
                  onBlur={() => {
                    // A search still in flight should not reopen a field the user left.
                    searchIndexRef.current = null;
                    setTimeout(() => {
                      setSuggestionsState((prev) => ({
                        ...prev,
                        [index]: { ...prev[index], show: false }
                      }));
                    }, 150); // small delay for selection
                  }}
                  placeholder="Type drug name"
                />
                {suggestionsState[index]?.show &&
                  suggestionsState[index].suggestions.length > 0 && (
                    <ul className="dropdown-suggestions">
                      {suggestionsState[index].suggestions.map((drug, i) => (
                        <li
                          key={i}
                          onClick={() => {
                            handleMedicationChange(index, 'name', drug);
                            setSuggestionsState((prev) => ({
                              ...prev,
                              [index]: { ...prev[index], show: false }
                            }));
                            setFilteredDrugs([]);
                          }}
                        >
                          {drug}
                        </li>
                      ))}
                    </ul>
                  )}
              </div>
              <div className="medication-field">
                <label id={`dose-label-${index}`}>Dose</label>
                <div className="dose-options" role="group" aria-labelledby={`dose-label-${index}`}>
                  {['½', '1', '2'].map((val) => (
                    <button
                      key={val}
                      type="button"
                      aria-pressed={med.dose === val}
                      className={`dose-btn ${med.dose === val ? 'selected' : ''}`}
                      onClick={() => handleMedicationChange(index, 'dose', val)}
                    >
                      {val}
                    </button>
                  ))}
                  <input
                    type="text"
                    placeholder="Custom dose"
                    aria-label={`Custom dose for medication ${index + 1}`}
                    value={['½', '1', '2'].includes(med.dose) ? '' : med.dose}
                    onChange={(e) => handleMedicationChange(index, 'dose', e.target.value)}
                  />
                </div>
              </div>
              <div className="medication-field">
                <label htmlFor={`med-duration-${index}`}>Duration</label>
                <div className="duration-input-group">
                  <input
                    type="number"
                    id={`med-duration-${index}`}
                    placeholder="Number"
                    value={med.durationValue || ''}
                    onChange={(e) => handleMedicationChange(index, 'durationValue', e.target.value)}
                    min="1"
                    required
                  />
                  <div className="duration-units">
                    {['Days', 'Weeks', 'Months', 'Years'].map((unit) => (
                      <button
                        key={unit}
                        type="button"
                        aria-pressed={med.durationUnit === unit}
                        className={`unit-btn ${med.durationUnit === unit ? 'selected' : ''}`}
                        onClick={() => handleMedicationChange(index, 'durationUnit', unit)}
                      >
                        {unit}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
              <div className="medication-field">
                <label id={`frequency-label-${index}`}>Frequency</label>
                <div
                  className="frequency-buttons"
                  role="group"
                  aria-labelledby={`frequency-label-${index}`}
                >
                  {['OD-Morning', 'OD-Night', 'BD', 'TID', 'QID', 'SOS'].map((freq) => (
                    <button
                      type="button"
                      key={freq}
                      aria-pressed={med.frequency === freq}
                      className={`freq-button ${med.frequency === freq ? 'selected' : ''}`}
                      onClick={() => handleMedicationChange(index, 'frequency', freq)}
                    >
                      {freq}
                    </button>
                  ))}
                </div>
              </div>
              {medications.length > 1 && (
                <button
                  type="button"
                  className="remove-btn"
                  aria-label={`Remove medication ${index + 1}`}
                  onClick={() => removeMedicationField(index)}
                >
                  <Icon name="close" size={16} /> Remove
                </button>
              )}
            </div>
          ))}
          <button type="button" className="add-btn" onClick={addMedicationField}>
            + Add another medication
          </button>
        </div>

        <div className="prescription-section-heading">
          <span>03</span>
          <div>
            <h2>Care instructions</h2>
            <p>Add remarks or follow-up instructions for your patient.</p>
          </div>
        </div>
        <Input
          id="remark"
          element="textarea"
          label="Remarks"
          placeholder="Enter care instructions and follow-up notes"
          validators={[VALIDATOR_MINLENGTH(5)]}
          errorText="Please enter remarks"
          onInput={inputHandler}
          initialValue={formState.inputs.remark.value}
          initialValid={formState.inputs.remark.isValid}
        />
        <div className="prescription-form-actions">
          <Button type="submit" disabled={!formState.isValid || isLoading}>
            {isLoading ? 'Checking...' : 'Check prescription'}
          </Button>
          <Button
            inverse
            type="button"
            onClick={async () => {
              await savePrescription();
              setTimeout(() => {}, 100);
            }}
            disabled={!formState.isValid || isLoading}
          >
            Save & print without check
          </Button>
        </div>
      </form>
      <VitalsModal
        show={showVitalsModal}
        onCancel={closeVitalsModal}
        onSave={saveVitalsHandler}
        vitalsForm={vitalsForm}
        setVitalsForm={setVitalsForm}
      />
      <NotesModal show={showNotes} onCancel={() => setShowNotes(false)} patientId={patientId} />
    </React.Fragment>
  );
};

export default UpdatePatient;
